import { ApiFetcherArgs } from "@ts-rest/core";
import { initQueryClient } from "@ts-rest/react-query";
import axios, { AxiosRequestConfig, AxiosResponse, isAxiosError, Method } from "axios";

import { queryClient } from "#app/http/ReactQueryProvider";
import { AccessTokenSingleton } from "#modules/auth/domain/AccessTokenSingleton";
import { isUserDisabledError } from "#modules/auth/domain/isUserDisabledError";
import { handleDisabledUser } from "#modules/auth/infra/handleDisabledUser";
import { confirmOptimizationInvalidation } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import {
  businessContract,
  coreContract,
  optimizationsContract,
  quoteRequestLinesContract,
  quoteRequestsContract,
  quoteRequestsV2Contract,
  searchContract,
  OPTIMIZATION_INVALIDATION_CONFIRMATION_HEADER,
} from "shared";

export const contract = {
  ...coreContract,
  ...businessContract,
  optimizations: optimizationsContract,
  quoteRequests: quoteRequestsContract,
  quoteRequestsV2: quoteRequestsV2Contract,
  quoteRequestLines: quoteRequestLinesContract,
  search: searchContract,
};

const toApiResponse = (response: AxiosResponse) => ({
  status: response.status,
  body: response.data,
  headers: response.headers as unknown as Headers,
});

const settleAsApiResponse = (error: unknown) => {
  if (isAxiosError(error) && error.response) {
    return toApiResponse(error.response);
  }
  throw error;
};

export const api = initQueryClient(contract, {
  baseUrl: import.meta.env.VITE_BASE_API_URL,
  baseHeaders: {},
  jsonQuery: true,
  api: async ({
    method,
    path,
    headers,
    body,
    responseType,
  }: ApiFetcherArgs & {
    timeout?: number;
    responseType?: AxiosRequestConfig["responseType"];
  }) => {
    const accessToken = AccessTokenSingleton.getInstance().getAccessToken();

    const send = (extraHeaders: Record<string, string> = {}) =>
      axios
        .request({
          method: method as Method,
          url: path,
          headers: {
            ...headers,
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            "x-page-url": window.location.href,
            ...extraHeaders,
          },
          data: body,
          responseType,
        })
        .then(toApiResponse, settleAsApiResponse);

    const response = await send();

    if (isUserDisabledError(response.status, response.body)) {
      void handleDisabledUser();
    }

    const isConfirmed = await confirmOptimizationInvalidation(response.status, response.body);
    if (!isConfirmed) {
      return response;
    }

    const confirmedResponse = await send({
      [OPTIMIZATION_INVALIDATION_CONFIRMATION_HEADER]: "true",
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.optimizations.all });
    return confirmedResponse;
  },
});
